<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <div class="d-flex align-items-center user-member__title">
                            <h4 class="text-capitalize fw-500 breadcrumb-title">
                                <i class="fas fa-plug"></i> Koneksi Device
                            </h4>
                        </div>
                        <div class="action-btn d-flex align-items-center">
                            <label class="mb-0 mr-3 small text-muted">
                                <input type="checkbox" v-model="autoRefresh" class="mr-1" />
                                Auto-refresh (45s)
                            </label>
                            <select v-model.number="disconnectionHours" @change="loadAll" class="form-control form-control-sm mr-2" style="width: auto;">
                                <option :value="2">Putus 2 jam</option>
                                <option :value="6">Putus 6 jam</option>
                                <option :value="12">Putus 12 jam</option>
                                <option :value="24">Putus 24 jam</option>
                            </select>
                            <button @click="loadAll" class="btn px-15 btn-secondary" :class="{ 'btn-loading': loading }">
                                <i class="fas fa-sync-alt"></i> Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="summary.disconnected > 0 && summary.connected_pct < 90" class="alert alert-warning">
                <b>{{ formatInt(summary.disconnected) }}</b> device tidak terkoneksi
                ({{ summary.connected_pct.toFixed(1) }}% terkoneksi).
                <span v-if="summary.pending_queue_total > 0">
                    Antrian <b>{{ formatInt(summary.pending_queue_total) }}</b> pesan menunggu device terkoneksi.
                </span>
            </div>

            <div v-if="unhealthyNodes.length > 0" class="alert alert-danger mb-30">
                <div class="d-flex flex-wrap justify-content-between align-items-start">
                    <div class="mb-2 mb-md-0">
                        <b>{{ unhealthyNodes.length }} node perlu tindakan</b>
                        <span class="d-block small mt-1 text-dark">
                            Offline, device banyak putus di node yang sama, atau response health lambat.
                            Pertimbangkan server/node baru, hide node rusak, atau pindah device ke node sehat.
                        </span>
                    </div>
                    <div class="d-flex flex-wrap">
                        <router-link to="/admin/node-server" class="btn btn-sm btn-outline-dark mr-2 mb-1">
                            Kelola Node Server
                        </router-link>
                        <router-link to="/admin/node-server-monitoring" class="btn btn-sm btn-outline-dark mb-1">
                            Node Monitor
                        </router-link>
                    </div>
                </div>
            </div>

            <div v-if="unhealthyNodes.length > 0" class="row mb-30">
                <div class="col-lg-12">
                    <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30 unhealthy-panel">
                        <h5 class="mb-3 text-danger"><i class="fas fa-exclamation-triangle"></i> Node tidak sehat</h5>
                        <div class="table-responsive">
                            <table class="table table-sm mb-0 table-hover">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>URL</th>
                                        <th>Health</th>
                                        <th>Device OK / Putus</th>
                                        <th>% OK</th>
                                        <th>Response</th>
                                        <th>Terakhir offline</th>
                                        <th>Alasan</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="node in unhealthyNodes" :key="'bad-' + node.server_id" class="row-unhealthy">
                                        <td><b>{{ node.server_id }}</b></td>
                                        <td><span class="server-url">{{ node.server_url }}</span></td>
                                        <td><span class="badge" :class="healthBadge(node.node_health)">{{ node.node_health || 'unknown' }}</span></td>
                                        <td>
                                            <span class="text-success">{{ formatInt(node.connected) }}</span>
                                            /
                                            <span class="text-danger">{{ formatInt(node.disconnected) }}</span>
                                        </td>
                                        <td :class="pctClass(node.connected_pct)">{{ node.connected_pct.toFixed(1) }}%</td>
                                        <td>{{ node.response_time ? node.response_time + ' ms' : '-' }}</td>
                                        <td>{{ formatTime(node.last_offline_at) }}</td>
                                        <td>
                                            <span v-for="(issue, idx) in node.health_issues" :key="idx" class="d-block small issue-line">
                                                {{ issue }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mb-30">
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Total Device</p>
                            <h3 class="metric-card__count fw-500">{{ formatInt(summary.total_devices) }}</h3>
                        </div>
                        <div class="metric-card__icon text-primary"><i class="fas fa-mobile-alt"></i></div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Terkoneksi</p>
                            <h3 class="metric-card__count fw-500 text-success">{{ formatInt(summary.connected) }}</h3>
                        </div>
                        <div class="metric-card__icon text-success"><i class="fas fa-check-circle"></i></div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Tidak Terkoneksi</p>
                            <h3 class="metric-card__count fw-500 text-danger">{{ formatInt(summary.disconnected) }}</h3>
                        </div>
                        <div class="metric-card__icon text-danger"><i class="fas fa-times-circle"></i></div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">% Terkoneksi</p>
                            <h3 class="metric-card__count fw-500" :class="pctClass(summary.connected_pct)">
                                {{ summary.connected_pct.toFixed(1) }}%
                            </h3>
                        </div>
                        <div class="metric-card__icon text-info"><i class="fas fa-percentage"></i></div>
                    </div>
                </div>
            </div>

            <div class="row mb-30">
                <div class="col-md-6">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Antrian Menunggu Device</p>
                            <h3 class="metric-card__count fw-500 text-warning">{{ formatInt(summary.pending_queue_total) }}</h3>
                            <small class="text-muted">{{ formatInt(summary.pending_device_count) }} device terdampak</small>
                        </div>
                        <div class="metric-card__icon text-warning"><i class="fas fa-hourglass-half"></i></div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="metric-card bg-white radius-xl p-20 border">
                        <div class="metric-card__content">
                            <p class="metric-card__title text-light-gray fs-14 text-uppercase">Terakhir Diperbarui</p>
                            <h3 class="metric-card__count fw-500" style="font-size:1.1rem;">{{ formatTime(generatedAt) }}</h3>
                        </div>
                        <div class="metric-card__icon text-secondary"><i class="fas fa-clock"></i></div>
                    </div>
                </div>
            </div>

            <div class="row mb-30">
                <div class="col-lg-12">
                    <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
                        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
                            <h5 class="mb-0">Per Node Server</h5>
                            <label class="mb-0 small text-muted">
                                <input type="checkbox" v-model="nodesUnhealthyOnly" class="mr-1" />
                                Tampilkan hanya node bermasalah
                            </label>
                        </div>
                        <div class="table-responsive">
                            <table class="table mb-0 table-hover">
                                <thead>
                                    <tr>
                                        <th>Server ID</th>
                                        <th>Node URL</th>
                                        <th>Health Node</th>
                                        <th>Total Device</th>
                                        <th>Terkoneksi</th>
                                        <th>Putus</th>
                                        <th>% OK</th>
                                        <th>Response</th>
                                        <th>Antrian Pending</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="node in displayedNodes"
                                        :key="'node-' + node.server_id"
                                        :class="{ 'row-unhealthy': node.is_unhealthy }"
                                    >
                                        <td><b>{{ node.server_id || '-' }}</b></td>
                                        <td><span class="server-url">{{ node.server_url }}</span></td>
                                        <td>
                                            <span class="badge" :class="healthBadge(node.node_health)">{{ node.node_health || 'unknown' }}</span>
                                            <span v-if="node.is_unhealthy" class="badge badge-warning ml-1" title="Perlu tindakan">!</span>
                                        </td>
                                        <td>{{ formatInt(node.total_devices) }}</td>
                                        <td class="text-success">{{ formatInt(node.connected) }}</td>
                                        <td class="text-danger">{{ formatInt(node.disconnected) }}</td>
                                        <td :class="pctClass(node.connected_pct)">{{ node.connected_pct.toFixed(1) }}%</td>
                                        <td>{{ node.response_time ? node.response_time + ' ms' : '-' }}</td>
                                        <td>{{ formatInt(node.pending_queue) }}</td>
                                    </tr>
                                    <tr v-if="displayedNodes.length === 0 && !loading">
                                        <td colspan="9" class="text-center py-4 text-muted">Tidak ada data node server</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mb-30">
                <div class="col-lg-6">
                    <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
                        <h5 class="mb-3">Antrian Pending per Device (Top 50)</h5>
                        <p v-if="pendingLoading" class="small text-muted mb-2">Memuat antrian pending (query berat, mohon tunggu)...</p>
                        <div class="table-responsive">
                            <table class="table table-sm mb-0 table-hover">
                                <thead>
                                    <tr>
                                        <th>Device</th>
                                        <th>Status</th>
                                        <th>Server</th>
                                        <th class="text-right">Pending</th>
                                        <th>User</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in pendingByDevice" :key="'pending-' + row.device_id">
                                        <td>
                                            <div><b>{{ row.device_name || ('#' + row.device_id) }}</b></div>
                                            <small class="text-muted">{{ row.no_hp || '-' }}</small>
                                        </td>
                                        <td><span class="badge badge-danger">{{ row.device_status || 'putus' }}</span></td>
                                        <td>{{ row.server_id || '-' }}</td>
                                        <td class="text-right"><b>{{ formatInt(row.pending_count) }}</b></td>
                                        <td>
                                            <router-link v-if="row.owner_id" :to="'/admin/user/' + row.owner_id" class="small">
                                                {{ row.owner_email || ('User #' + row.owner_id) }}
                                            </router-link>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                    </tr>
                                    <tr v-if="pendingByDevice.length === 0 && !loading">
                                        <td colspan="5" class="text-center py-4 text-muted">Tidak ada antrian pending</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="col-lg-6">
                    <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
                        <h5 class="mb-3">Device Putus Terbaru ({{ disconnectionHours }} jam)</h5>
                        <div class="table-responsive">
                            <table class="table table-sm mb-0 table-hover">
                                <thead>
                                    <tr>
                                        <th>Device</th>
                                        <th>Status</th>
                                        <th>Server</th>
                                        <th>Updated</th>
                                        <th>User</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in recentDisconnections" :key="'recent-' + row.device_id">
                                        <td>
                                            <div><b>{{ row.device_name || ('#' + row.device_id) }}</b></div>
                                            <small class="text-muted">{{ row.no_hp || '-' }}</small>
                                        </td>
                                        <td><span class="badge badge-danger">{{ row.status || 'putus' }}</span></td>
                                        <td>{{ row.server_id || '-' }}</td>
                                        <td>{{ formatTime(row.updated_at) }}</td>
                                        <td>
                                            <router-link v-if="row.owner_id" :to="'/admin/user/' + row.owner_id" class="small">
                                                {{ row.owner_email || ('User #' + row.owner_id) }}
                                            </router-link>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                    </tr>
                                    <tr v-if="recentDisconnections.length === 0 && !loading">
                                        <td colspan="5" class="text-center py-4 text-muted">Tidak ada device putus dalam rentang waktu ini</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    data() {
        return {
            loading: false,
            autoRefresh: true,
            disconnectionHours: 24,
            generatedAt: 0,
            summary: {
                total_devices: 0,
                connected: 0,
                disconnected: 0,
                connected_pct: 0,
                pending_queue_total: 0,
                pending_device_count: 0,
            },
            nodes: [],
            unhealthyNodes: [],
            pendingByDevice: [],
            recentDisconnections: [],
            pendingLoading: false,
            nodesUnhealthyOnly: false,
            refreshTimer: null,
        };
    },
    computed: {
        displayedNodes() {
            if (!this.nodesUnhealthyOnly) {
                return this.nodes;
            }
            return this.nodes.filter((n) => n.is_unhealthy);
        },
    },
    mounted() {
        this.loadAll();
        this.refreshTimer = setInterval(() => {
            if (this.autoRefresh) {
                this.loadAll(true);
            }
        }, 45000);
    },
    beforeDestroy() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
    },
    methods: {
        async loadAll(silent) {
            if (!silent) {
                this.loading = true;
            }
            try {
                const res = await axios.get('admin/device-connection/summary', {
                    params: { hours: this.disconnectionHours },
                });
                const data = res.data.data || {};
                this.summary = data.summary || this.summary;
                this.nodes = data.nodes || [];
                this.unhealthyNodes = data.unhealthy_nodes || [];
                this.recentDisconnections = data.recent_disconnections || [];
                this.generatedAt = data.generated_at || Date.now();
            } catch (e) {
                console.error(e);
                if (!silent) {
                    this.$toast && this.$toast.error('Gagal memuat data koneksi device');
                }
            } finally {
                this.loading = false;
            }
            this.loadPending(silent);
        },
        async loadPending(silent) {
            this.pendingLoading = true;
            try {
                const res = await axios.get('admin/device-connection/pending', {
                    params: { limit: 50 },
                    timeout: 300000,
                });
                const data = res.data.data || {};
                this.pendingByDevice = data.pending_by_device || [];
            } catch (e) {
                console.error(e);
                if (!silent) {
                    this.$toast && this.$toast.error('Gagal memuat antrian pending per device');
                }
            } finally {
                this.pendingLoading = false;
            }
        },
        formatInt(n) {
            return Number(n || 0).toLocaleString('id-ID');
        },
        formatTime(ms) {
            if (!ms) return '-';
            return new Date(Number(ms)).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' });
        },
        pctClass(pct) {
            if (pct >= 95) return 'text-success';
            if (pct >= 80) return 'text-warning';
            return 'text-danger';
        },
        healthBadge(status) {
            if (status === 'online') return 'badge-success';
            if (status === 'offline') return 'badge-danger';
            return 'badge-secondary';
        },
    },
};
</script>

<style scoped>
.metric-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.metric-card__icon {
    font-size: 2rem;
    opacity: 0.3;
}
.metric-card__count {
    font-size: 1.75rem;
    margin: 0;
}
.metric-card__title {
    margin: 0 0 5px 0;
}
.server-url {
    font-family: monospace;
    font-size: 0.85rem;
    color: #666;
}
.badge-success { background-color: #00c853; color: white; }
.badge-danger { background-color: #ff1744; color: white; }
.badge-secondary { background-color: #6c757d; color: white; }
.text-success { color: #00c853 !important; }
.text-danger { color: #ff1744 !important; }
.text-warning { color: #ff9100 !important; }
.btn-loading { pointer-events: none; opacity: 0.7; }
.unhealthy-panel { border-left: 4px solid #ff1744 !important; }
.row-unhealthy { background-color: #fff5f5; }
.badge-warning { background-color: #ff9100; color: #fff; }
.issue-line { line-height: 1.35; color: #555; }
</style>
